import { useState } from 'react';
import './printSetupBanner.css';
import { acknowledgeSetUp, downloadSetupScript, hidePrintSetupToday } from './printSetup';
import { ChromeTooOld, PrintSetupSteps } from './PrintSetupSteps';
import { usePrintSetup } from './usePrintSetup';

/*
 * The packing-PC print setup reminder on every Fulfillment screen (SSD-7389). A PC the setup file has set up carries
 * its done mark (see printSetup.ts) and never sees the reminder; any other PC is asked to download and run the file,
 * and can wave the reminder off for the rest of the day. View Orders' settings show the same state and can bring the
 * reminder back.
 */

interface PrintSetupBannerProps {
  now?: () => Date;
}

export const PrintSetupBanner = ({ now = () => new Date() }: PrintSetupBannerProps) => {
  const setup = usePrintSetup(now);
  const [stepsOpen, setStepsOpen] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  if (setup.justSetUp) {
    return (
      <section className="ff-print-setup ff-print-setup-ready" role="note" aria-label="Label printer setup">
        <div className="ff-print-setup-row">
          <span className="ff-print-setup-text">
            <b>Label printing is set up on this PC.</b> Print one order from View Orders: the label should come out with
            no print dialog.
          </span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={acknowledgeSetUp}>
            OK
          </button>
        </div>
      </section>
    );
  }

  if (setup.done || setup.hiddenToday) {
    return null;
  }

  return (
    <section className="ff-print-setup" role="note" aria-label="Label printer setup">
      <div className="ff-print-setup-row">
        <span className="ff-print-setup-text">
          <b>Label printing:</b> this PC is not set up to print labels straight to the label printer. Download the setup
          file and run it once.
        </span>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => {
            downloadSetupScript();
            setDownloaded(true);
            setStepsOpen(true);
          }}
        >
          {downloaded ? 'Download again' : 'Download setup file (Windows)'}
        </button>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setStepsOpen((was) => !was)}>
          {stepsOpen ? 'Hide steps' : 'Steps'}
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => hidePrintSetupToday(now())}>
          Don&apos;t show today
        </button>
      </div>
      <ChromeTooOld />
      {stepsOpen && <PrintSetupSteps />}
    </section>
  );
};

export default PrintSetupBanner;
